import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_OG_IMAGE, metaForPath } from './meta';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

/**
 * Keeps <head> in sync with the current route during client-side navigation
 * (the first load already has these tags from the prerendered HTML).
 */
export function RouteMeta({ title }: { title?: string }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = metaForPath(pathname);
    const origin = window.location.origin;
    const url = `${origin}${meta.canonicalPath ?? (pathname.replace(/\/+$/, '') || '/')}`;
    const pageTitle = title ?? meta.title;
    document.title = pageTitle;
    setMeta('name', 'description', meta.description);
    setMeta(
      'name',
      'robots',
      meta.index ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    );
    if (meta.index || meta.canonicalPath) setCanonical(url);
    else document.head.querySelector('link[rel="canonical"]')?.remove();
    setMeta('property', 'og:title', pageTitle);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', `${origin}${meta.ogImage ?? DEFAULT_OG_IMAGE}`);
    setMeta('name', 'twitter:title', pageTitle);
    setMeta('name', 'twitter:description', meta.description);
  }, [pathname, title]);
  return null;
}
